import { z } from "zod";

export const deliveryOptionFormSchema = z
  .object({
    name: z
      .string()
      .min(1, "Display name is required")
      .max(100, "Display name must be 100 characters or less"),
    defaultClientPrice: z
      .number()
      .min(0, "Price must be 0 or greater (0 = free)")
      .max(10_000, "Price must be 10 000 or less"),
    visibilityMode: z.enum(["GLOBAL", "PARTNER_LIST"]),
    requireInput: z.boolean(),
    requiresFile: z.boolean(),
    requiresMultipleFiles: z.boolean(),
    dynamicPricing: z.boolean(),
    // Always present in form state (null when no file required). Normalize API → form in toFormValues.
    fileType: z.enum(["IMAGE", "PDF"]).nullable(),
    active: z.boolean(),
    partnerAssignments: z.array(
      z.object({
        organizationId: z.string(),
        selected: z.boolean(),
        enabled: z.boolean(),
        displayName: z.string(),
        clientPrice: z.number().nullable(),
      })
    ),
  })
  .superRefine((data, ctx) => {
    if (
      data.visibilityMode === "PARTNER_LIST" &&
      !data.partnerAssignments.some((a) => a.selected)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one partner must be enabled when using Partner List mode",
        path: ["partnerAssignments"],
      });
    }

    if (data.visibilityMode === "PARTNER_LIST") {
      data.partnerAssignments.forEach((a, i) => {
        if (a.selected && !a.displayName.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Display name is required",
            path: ["partnerAssignments", i, "displayName"],
          });
        }
        const effectivePrice = a.clientPrice ?? data.defaultClientPrice;
        if (
          a.selected &&
          (effectivePrice === null || effectivePrice === undefined || effectivePrice < 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Client price must be 0 or greater",
            path: ["partnerAssignments", i, "clientPrice"],
          });
        }
      });
    }

    if (data.requiresFile && !data.fileType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "File type is required when file is required",
        path: ["fileType"],
      });
    }

    if (!data.requiresFile && data.fileType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "File type must be null when file is not required",
        path: ["fileType"],
      });
    }
  });

export type DeliveryOptionFormValues = z.infer<typeof deliveryOptionFormSchema>;
export type DeliveryOptionPartnerAssignmentFormValues =
  DeliveryOptionFormValues["partnerAssignments"][number];

export const defaultValues: DeliveryOptionFormValues = {
  name: "",
  defaultClientPrice: 0,
  visibilityMode: "GLOBAL",
  requireInput: false,
  requiresFile: false,
  requiresMultipleFiles: false,
  dynamicPricing: false,
  fileType: null,
  active: true,
  partnerAssignments: [],
};
